import { useCallback, useMemo } from 'react'
import {
  useListAssociatedParties,
  useListAssociatedPartiesForProduct,
  useAssignAssociatedPartyToProduct,
  useUnassignAssociatedPartyFromProduct,
  type AssociatedParty,
  type AssociatedPartyType,
} from '@/shared/hooks/api/associatedParties'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { formatPartyCityCountry } from '@/features/associated-parties/formatAddress'
import { useCountryDisplayName } from '@/features/associated-parties/useCountryDisplayName'

type AssignPartyModalProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  productId: string
  partyType: AssociatedPartyType
  /** When changing (single-slot cards), the currently assigned party id (to unassign before assigning new). Omit for CONSULTANT (multiple allowed). */
  currentPartyId?: string | null
  /** When changing an existing manufacturer, parent shows confirmation and performs the update. */
  onRequestManufacturerChange?: (party: AssociatedParty) => void
}

export function AssignPartyModal({
  open,
  onOpenChange,
  productId,
  partyType,
  currentPartyId,
  onRequestManufacturerChange,
}: AssignPartyModalProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const getCountryName = useCountryDisplayName()
  const { data: orgParties = [], isLoading: isLoadingOrg } =
    useListAssociatedParties({ enabled: open })
  const { data: assignedParties = [] } =
    useListAssociatedPartiesForProduct(productId)
  const assignMutation = useAssignAssociatedPartyToProduct(productId)
  const unassignMutation = useUnassignAssociatedPartyFromProduct(productId)
  const isSaving = assignMutation.isPending || unassignMutation.isPending

  const filteredParties = useMemo(() => {
    if (partyType === 'CONSULTANT') {
      const consultantIds = new Set(
        assignedParties
          .filter((assignment) => assignment.role === 'CONSULTANT')
          .map((assignment) => assignment.associatedParty.id)
      )
      return orgParties.filter((party) => !consultantIds.has(party.id))
    }
    return orgParties
  }, [orgParties, partyType, assignedParties])

  const assignedIdsForRole = useMemo(
    () =>
      new Set(
        assignedParties
          .filter((assignment) => assignment.role === partyType)
          .map((assignment) => assignment.associatedParty.id)
      ),
    [assignedParties, partyType]
  )

  const persistSelection = useCallback(
    async (party: AssociatedParty) => {
      if (isSaving) {
        return
      }
      try {
        if (party.id === currentPartyId) {
          onOpenChange(false)
          return
        }
        if (currentPartyId && currentPartyId !== party.id) {
          await unassignMutation.mutateAsync({
            associatedPartyId: currentPartyId,
            role: partyType,
          })
        }
        await assignMutation.mutateAsync({
          associatedPartyId: party.id,
          role: partyType,
        })
        let successKey = 'settings.associatedParties.assignSuccessConsultant'
        if (partyType === 'MANUFACTURER') {
          successKey = 'settings.associatedParties.assignSuccessManufacturer'
        }
        if (partyType === 'AUTHORISED_REPRESENTATIVE') {
          successKey = 'settings.associatedParties.assignSuccessAuthorisedRep'
        }
        toast.success(t(successKey))
        onOpenChange(false)
      } catch (error) {
        console.error(error)
        toast.error(t('settings.associatedParties.assignError'))
      }
    },
    [
      currentPartyId,
      partyType,
      assignMutation,
      unassignMutation,
      isSaving,
      onOpenChange,
      t,
    ]
  )

  const handleSelect = useCallback(
    (party: AssociatedParty) => {
      const isManufacturerChange =
        partyType === 'MANUFACTURER' &&
        currentPartyId &&
        currentPartyId !== party.id

      if (isManufacturerChange && onRequestManufacturerChange) {
        onRequestManufacturerChange(party)
        onOpenChange(false)
        return
      }
      void persistSelection(party)
    },
    [
      currentPartyId,
      partyType,
      onRequestManufacturerChange,
      onOpenChange,
      persistSelection,
    ]
  )

  let titleKey = 'settings.associatedParties.modalTitleConsultant'
  if (partyType === 'MANUFACTURER') {
    titleKey = 'settings.associatedParties.modalTitleManufacturer'
  }
  if (partyType === 'AUTHORISED_REPRESENTATIVE') {
    titleKey = 'settings.associatedParties.modalTitleAuthorisedRep'
  }

  const handleValueChange = useCallback(
    (value: string) => {
      const party = filteredParties.find(
        (filteredParty) => filteredParty.id === value
      )
      if (party) {
        void handleSelect(party)
      }
    },
    [filteredParties, handleSelect]
  )
  const selectItems = useMemo(() => {
    return filteredParties.map((party) => {
      const isAssignedToThisRole = assignedIdsForRole.has(party.id)
      const location = formatPartyCityCountry(party, getCountryName)
      const label = [
        party.name,
        location,
        isAssignedToThisRole
          ? `(${t('settings.associatedParties.current')})`
          : null,
      ]
        .filter(Boolean)
        .join(' ')
      return { value: party.id, label }
    })
  }, [assignedIdsForRole, filteredParties, getCountryName, t])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='flex flex-col gap-4'>
        <DialogHeader>
          <DialogTitle>{t(titleKey)}</DialogTitle>
          <DialogDescription>
            {t('settings.associatedParties.modalDescription')}
          </DialogDescription>
        </DialogHeader>
        {isLoadingOrg ? (
          <p className='text-sm text-muted-foreground'>
            {t('settings.associatedParties.loading')}
          </p>
        ) : filteredParties.length === 0 ? (
          <div className='space-y-2 text-sm text-muted-foreground'>
            <p>{t('settings.associatedParties.noPartiesInOrg')}</p>
            <Button asChild variant='outline' size='sm'>
              <Link to='/associated-parties'>
                {t('settings.associatedParties.addPartyInOrg')}
              </Link>
            </Button>
          </div>
        ) : (
          <Select
            value={currentPartyId ?? ''}
            onValueChange={(value) => {
              if (value) {
                handleValueChange(value)
              }
            }}
            disabled={isSaving}
            items={selectItems}
          >
            <SelectTrigger className='w-full'>
              <SelectValue
                placeholder={t('settings.associatedParties.selectPlaceholder')}
              />
            </SelectTrigger>
            <SelectContent>
              {filteredParties.map((party) => {
                const isAssignedToThisRole = assignedIdsForRole.has(party.id)
                const location = formatPartyCityCountry(party, getCountryName)
                const label = [
                  party.name,
                  location,
                  isAssignedToThisRole
                    ? `(${t('settings.associatedParties.current')})`
                    : null,
                ]
                  .filter(Boolean)
                  .join(' ')
                return (
                  <SelectItem key={party.id} value={party.id}>
                    {label}
                  </SelectItem>
                )
              })}
            </SelectContent>
          </Select>
        )}
      </DialogContent>
    </Dialog>
  )
}
